import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Empty, Tag, Spin } from 'antd'
import type { StrategyVersion } from '@/types'
import { localToUTC } from '@/utils/filters'
import './index.scss'
import { marketOptions } from '@/pages/static'

type VersionListProps = {
    managerName: string
    strategyVersions: StrategyVersion[]
    reviewVersion: StrategyVersion[] | null
    loading: boolean
    onBack: () => void
    onCreate: () => void
    onOpenEdit: (id: number) => void
    onOpenReview: (id: number) => void
}

function getTagClassName(type: string) {
    if (type === 'HK') {
        return 'is-purple'
    }

    return 'is-success'
}

function getStatusDesc(status: string) {
    if (status === 'draft') {
        return { className: 'is-default', statusDesc: '草稿' }
    }
    if (status === 'pending') {
        return { className: 'is-warning', statusDesc: '待审批' }
    }
    if (status === 'active') {
        return { className: 'is-success', statusDesc: '已激活' }
    }
    if (status === 'archived') {
        return { className: 'is-default', statusDesc: '已归档' }
    }
    return { className: 'is-default', statusDesc: '--' }
}

export default function VersionList({
    managerName,
    strategyVersions,
    reviewVersion,
    loading,
    onBack,
    onCreate,
    onOpenEdit,
    onOpenReview,
}: VersionListProps) {
    return (
        <div className="strategy-version-page">
            <div className="page-header strategy-version-page__header">
                <div className="strategy-version-page__header-left">
                    <Button
                        type="text"
                        className="strategy-version-page__back color-2"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBack}
                    >
                        {managerName}
                    </Button>
                    <span>/</span>
                    <span className="strategy-version-page__crumb">策略版本管理</span>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onCreate}
                >
                    新建策略版本
                </Button>
            </div>

            <div className="page-content strategy-version-page__content">
                {loading ? <div className='page-loading'><Spin spinning={loading} /></div> : null}

                {!loading && reviewVersion && reviewVersion.length > 0
                    ? reviewVersion.map((item) => (
                        <section className="strategy-version-page__approval" key={item.id}>
                            <div className="strategy-version-page__approval-main">
                                <span>⏳</span>
                                <div className="strategy-version-page__approval-title">
                                    {item.version_no} {item.name} 正在等待审批
                                </div>
                            </div>
                            <div className="strategy-version-page__approval-actions">
                                <Button
                                    size="middle"
                                    type="primary"
                                    style={{ background: 'var(--green)' }}
                                    onClick={() => onOpenReview(item.id)}
                                >
                                    ✓ 通过
                                </Button>
                                <Button
                                    size="middle"
                                    danger
                                    style={{ color: 'var(--red)' }}
                                    onClick={() => onOpenReview(item.id)}
                                >
                                    ✗ 驳回
                                </Button>
                            </div>
                        </section>
                    ))
                    : null}

                {!loading ? (
                    <section className="strategy-version-page__list">
                        {strategyVersions.length ? strategyVersions.map((item) => (
                            <article
                                key={item.id}
                                className='strategy-version-page__card'
                                onClick={() => item.status === 'draft' ? onOpenEdit(item.id) : onOpenReview(item.id)}
                            >
                                <div className="strategy-version-page__version">{item.version_no}</div>
                                <div className="strategy-version-page__card-main grid gap-4">
                                    <div className="strategy-version-page__card-top">
                                        <div className="strategy-version-page__card-copy">
                                            <div className="strategy-version-page__card-title">{item.name}</div>
                                            <div className="strategy-version-page__card-desc">{item.strategy_snapshot}</div>
                                        </div>
                                    </div>

                                    <div className="strategy-version-page__card-meta">
                                        <span>创建于 {localToUTC(item.created_at)}</span>
                                        {item.activated_at && <span> · 激活 {localToUTC(item.activated_at)}</span>}
                                        {item.approved_by && <span> · 审批人：{item.approved_by}</span>}
                                    </div>
                                </div>
                                <div className="strategy-version-page__card-tags">
                                    <Tag className={`${getTagClassName(item.market)} text-10`}>
                                        {marketOptions[item.market as keyof typeof marketOptions]}
                                    </Tag>
                                    <Tag className={`is-round ${getStatusDesc(item.status).className} text-11`}>
                                        {getStatusDesc(item.status).statusDesc}
                                    </Tag>
                                    {(item.status === 'active' || item.status === 'archived') && (
                                        <Button
                                            size="small"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                onOpenEdit(item.id)
                                            }}
                                        >
                                            编辑
                                        </Button>
                                    )}
                                </div>
                            </article>
                        )) : (
                            <div className="strategy-version-page__empty">
                                <Empty description="暂无策略版本" />
                            </div>
                        )}
                    </section>
                ) : null}
            </div>
        </div>
    )
}
